import { useState } from 'react';
import { RunButton, InfoCard, NoResult, AiAdvisor } from './common';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { APP } from '@/constants/testIds';
import { toast } from 'sonner';
import { ExternalLink, Navigation } from 'lucide-react';

// AGMARKNET commodity labels for the 8 crops in the app.
const COMMODITIES = [
  'Rice',
  'Maize',
  'Groundnut',
  'Jowar(Sorghum)',
  'Cotton',
  'Arhar (Tur/Red Gram)(Whole)',
  'Sugarcane',
  'Bajra(Pearl Millet/Cumbu)',
];

const VEHICLES = [
  { key: 'tractor_trolley', label: 'Tractor + trolley (diesel, ~4 kmpl)' },
  { key: 'pickup_mini_truck', label: 'Pickup / mini-truck (diesel, ~12 kmpl)' },
  { key: 'two_wheeler_cart', label: 'Two-wheeler cart (petrol, ~35 kmpl)' },
];

const Market = ({ farm }) => {
  const [mandi, setMandi] = useState(null);
  const [mandiLoading, setMandiLoading] = useState(false);
  const [commodity, setCommodity] = useState('Rice');
  const [vehicle, setVehicle] = useState('pickup_mini_truck');
  const [qty, setQty] = useState('5');

  const runMandi = async () => {
    setMandiLoading(true);
    try {
      setMandi(await api.marketMandi({
        lat: farm.lat, lon: farm.lon, commodity, state: 'Telangana',
        vehicle, quantity_quintals: parseFloat(qty) || 1,
      }));
    } catch (e) { toast.error('Mandi advisory failed'); }
    finally { setMandiLoading(false); }
  };

  return (
    <InfoCard title="Live Mandi Price + Transport Cost Ranking"
              subtitle="Ported from farmer_market_advisory_rice.ipynb — fetches today's mandi prices via data.gov.in (AGMARKNET) and ranks markets by net price after fuel + transport.">
      <div className="grid gap-3 md:grid-cols-4 items-end">
        <div>
          <Label className="text-xs">Commodity</Label>
          <Select value={commodity} onValueChange={setCommodity}>
            <SelectTrigger data-testid={APP.mandi.commodity}><SelectValue /></SelectTrigger>
            <SelectContent>
              {COMMODITIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Vehicle</Label>
          <Select value={vehicle} onValueChange={setVehicle}>
            <SelectTrigger data-testid={APP.mandi.vehicle}><SelectValue /></SelectTrigger>
            <SelectContent>
              {VEHICLES.map(v => <SelectItem key={v.key} value={v.key}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Quantity (quintals)</Label>
          <Input type="number" data-testid={APP.mandi.quantity} value={qty} onChange={e => setQty(e.target.value)} />
        </div>
        <RunButton testId={APP.actions.runMarketMandi} onClick={runMandi} loading={mandiLoading}>Rank markets</RunButton>
      </div>

      {!mandi && !mandiLoading && <div className="mt-4"><NoResult /></div>}
      {mandi && (
        <div className="mt-5 space-y-4 animate-fade-up">
          <div className="text-xs text-muted-foreground">Data source: <b>{mandi.source}</b> · Fuel used: {mandi.fuel_type} @ ₹{mandi.fuel_price_per_litre}/L · Mileage: {mandi.mileage_kmpl} kmpl</div>

          {mandi.recommendation && (
            <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-300">
              <div className="text-sm text-emerald-800 uppercase tracking-wider font-medium">Recommended market</div>
              <div className="font-display text-2xl text-emerald-900 mt-1">{mandi.recommendation.market} · {mandi.recommendation.district}</div>
              <div className="text-sm text-emerald-900 mt-1">
                Modal ₹{mandi.recommendation.modal_price?.toLocaleString('en-IN')}/q · Distance {mandi.recommendation.distance_km} km ·
                Transport ₹{mandi.recommendation.transport_cost?.transport_cost_per_quintal_inr}/q ·
                <b> Net ₹{mandi.recommendation.net_price_per_quintal?.toLocaleString('en-IN')}/q</b>
              </div>
              <div className="mt-3 flex gap-2">
                {mandi.recommendation.directions_url && (
                  <Button asChild size="sm" variant="outline" className="border-emerald-700 text-emerald-800 hover:bg-emerald-700 hover:text-white">
                    <a href={mandi.recommendation.directions_url} target="_blank" rel="noreferrer"><Navigation className="h-3.5 w-3.5 mr-1" />Directions</a>
                  </Button>
                )}
                {mandi.recommendation.maps_view_url && (
                  <Button asChild size="sm" variant="outline">
                    <a href={mandi.recommendation.maps_view_url} target="_blank" rel="noreferrer"><ExternalLink className="h-3.5 w-3.5 mr-1" />Open in Maps</a>
                  </Button>
                )}
              </div>
            </div>
          )}

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Market</TableHead>
                <TableHead className="hidden md:table-cell">District</TableHead>
                <TableHead className="hidden md:table-cell">Variety</TableHead>
                <TableHead className="text-right">Modal ₹/q</TableHead>
                <TableHead className="text-right">Distance</TableHead>
                <TableHead className="text-right">Transport ₹/q</TableHead>
                <TableHead className="text-right">Net ₹/q</TableHead>
                <TableHead>Route</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {mandi.results.map((r, i) => (
                <TableRow key={`${r.market}-${r.variety || 'na'}-${r.arrival_date || i}`} className={i === 0 && r.net_price_per_quintal ? 'bg-emerald-50/50' : ''}>
                  <TableCell className="font-medium">{r.market}</TableCell>
                  <TableCell className="hidden md:table-cell">{r.district}</TableCell>
                  <TableCell className="hidden md:table-cell">{r.variety || '—'}</TableCell>
                  <TableCell className="text-right">{r.modal_price?.toLocaleString('en-IN') || '—'}</TableCell>
                  <TableCell className="text-right">{r.distance_km ? `${r.distance_km} km` : <Badge variant="outline">unknown</Badge>}</TableCell>
                  <TableCell className="text-right">{r.transport_cost?.transport_cost_per_quintal_inr ?? '—'}</TableCell>
                  <TableCell className="text-right font-medium text-icrisat-green">{r.net_price_per_quintal?.toLocaleString('en-IN') || '—'}</TableCell>
                  <TableCell>
                    {r.directions_url && (
                      <Button asChild size="sm" variant="ghost" className="h-8">
                        <a href={r.directions_url} target="_blank" rel="noreferrer"><Navigation className="h-3.5 w-3.5" /></a>
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <AiAdvisor context="market_mandi" payload={mandi} />
        </div>
      )}
    </InfoCard>
  );
};

export default Market;
